/**
 * 자료원·도구 리본 — 에이전트가 무엇을 쓰고 있는지 한눈에 보여 주고, 끄고 켤 수 있다.
 *
 * **두 줄로 가른다**(사용자 지적 2026-09-21). 기준은 「읽는 것」과 「하는 것」이다:
 *   · 참고 자료 — 이미 있는 것을 읽는다(카탈로그·주석·모델·사전·관계·실행 SQL·튜닝 사례)
 *   · 실행 도구 — DB 에 **무언가를 실행해 새로 만든다**(실행계획 실측·임시 인덱스)
 * 예전에는 둘을 「메타 정보」 한 줄에 뭉쳤는데, 실행계획 분석은 메타데이터가 아니라 행위이고
 * 샘플 행·사례도 "메타 정보" 라는 말에 맞지 않았다.
 *
 * **전부 나열한다.** 있는 것만 보여 주면 "왜 모델을 안 쓰지?" 를 알 수가 없다.
 * 세 상태를 구분한다:
 *   · 사용 중   — 이 환경에 있고 켜져 있다
 *   · 사용 안 함 — 있지만 사용자가 껐다 (다시 켤 수 있다)
 *   · 미존재    — 이 환경에 없다. **왜 없는지**를 툴팁으로 준다 — 그래야 손을 쓸 수 있다
 *
 * 끈 정보원은 서버가 **도구 자체를 등록하지 않는다.** 프롬프트로 "쓰지 마라" 고만 하면
 * 모델이 무시할 수 있다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiSlash, FiMinusCircle, FiHelpCircle, FiRefreshCw } from 'react-icons/fi';
import { getMetaSources } from '../../services/assistantClient';
import styles from './AssistantPanel.module.css';

/**
 * **행위**인 것들 — DB 에 실행해 새로 만든다. 나머지는 읽기만 한다.
 * 서버(`metaSources.js`)의 id 를 그대로 쓴다.
 */
const TOOL_IDS = new Set(['tuning']);

function MetaSourceRibbon({ connectionId, dataModelId, stdNm, disabled, onDisabledChange }) {
    const { t } = useTranslation();
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getMetaSources({ connectionId, dataModelId, stdNm });
            setTypes(r.types || []);
        } catch (_e) {
            setTypes([]);          // 리본이 못 그려도 대화는 되어야 한다
        } finally {
            setLoading(false);
        }
    }, [connectionId, dataModelId, stdNm]);

    useEffect(() => { load(); }, [load]);

    const toggle = useCallback((id) => {
        onDisabledChange(disabled.includes(id) ? disabled.filter(x => x !== id) : [...disabled, id]);
    }, [disabled, onDisabledChange]);

    if (types.length === 0) return null;

    /** 칩 하나 — 두 묶음이 같은 모양을 써야 한다(베끼면 한쪽만 고치는 일이 생긴다) */
    const renderChip = (s) => {
                const off = disabled.includes(s.id);
                const missing = s.state === 'missing';
                const cond = s.state === 'conditional';
                // 조건부를 초록으로 칠하면 쓰고 있는 것처럼 보인다 — 따로 그린다
                const state = missing ? 'missing' : (off ? 'off' : (cond ? 'cond' : 'on'));
                const title = (missing || cond)
                    ? `${t(`assistant:meta.${s.id}`)} — ${t(missing ? 'assistant:meta.missing' : 'assistant:meta.conditional')}: ${s.reason || ''}`
                    : `${t(`assistant:meta.${s.id}`)}${s.detail ? ` — ${s.detail}` : ''}`
                        + (s.canDisable ? `\n${t(off ? 'assistant:meta.clickOn' : 'assistant:meta.clickOff')}` : `\n${t('assistant:meta.always')}`);
        return (
            <button
                key={s.id}
                type="button"
                className={`${styles.ribbonChip} ${styles[`ribbon_${state}`]}`}
                onClick={() => s.canDisable && toggle(s.id)}
                disabled={!s.canDisable}
                title={title}
            >
                {missing ? <FiMinusCircle size={9} /> : (off ? <FiSlash size={9} /> : (cond ? <FiHelpCircle size={9} /> : <FiCheck size={9} />))}
                {t(`assistant:meta.${s.id}`)}
            </button>
        );
    };

    const tools = types.filter(x => TOOL_IDS.has(x.id));
    const refs = types.filter(x => !TOOL_IDS.has(x.id));

    /** 라벨 + 칩 묶음 한 줄. 칩은 **라벨 오른쪽에서** 줄바꿈된다(내어쓰기). */
    const row = (labelKey, list, extra) => (list.length === 0 ? null : (
        <div className={styles.ribbonRow}>
            <span className={styles.ribbonLabel}>{t(labelKey)}</span>
            <div className={styles.ribbonChips}>{list.map(renderChip)}</div>
            {extra}
        </div>
    ));

    return (
        <div className={styles.ribbon}>
            {row('assistant:meta.label', refs, (
                <button
                    type="button" className={styles.ribbonRefresh}
                    onClick={load} disabled={loading} title={t('assistant:meta.refresh')}
                >
                    <FiRefreshCw size={10} />
                </button>
            ))}
            {row('assistant:meta.toolsLabel', tools, null)}
        </div>
    );
}

MetaSourceRibbon.propTypes = {
    connectionId: PropTypes.string,
    dataModelId: PropTypes.string,
    stdNm: PropTypes.string,
    disabled: PropTypes.array.isRequired,
    onDisabledChange: PropTypes.func.isRequired,
};

export default MetaSourceRibbon;
